import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { get } from "@/lib/api";
import type { CustomersOutput, DetailsOutput, SuggestOutput } from "@/lib/types";

export type Customer = CustomersOutput["customers"][number];
export interface Address { street: string; zip: string; city: string; countryCode: string }
export const emptyAddress: Address = { street: "", zip: "", city: "", countryCode: "AT" };

export function isAddressFilled(a: Address): boolean {
  return a.street.trim() !== "" && a.zip.trim() !== "" && a.city.trim() !== "" && a.countryCode.trim().length === 2;
}

export function useSuggest(q: string, session: string) {
  const [debounced, setDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);
  return useQuery({
    queryKey: ["suggest", debounced, session],
    queryFn: () => get<SuggestOutput>(`/api/customers/suggest?q=${encodeURIComponent(debounced)}&session=${encodeURIComponent(session)}`),
    enabled: debounced.length >= 2,
    staleTime: 60_000,
  });
}

export function fetchPlace(placeId: string, session: string): Promise<DetailsOutput> {
  return get<DetailsOutput>(`/api/places/${encodeURIComponent(placeId)}?session=${encodeURIComponent(session)}`);
}

export function AddressFields({ value, onChange }: { value: Address; onChange: (a: Address) => void }) {
  const set = (k: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...value, [k]: e.target.value });
  return (
    <div className="grid grid-cols-[1fr_6rem_1fr_4rem] gap-2">
      <div className="col-span-4 space-y-1.5"><Label>Straße</Label><Input value={value.street} onChange={set("street")} /></div>
      <div className="col-span-1 space-y-1.5"><Label>PLZ</Label><Input value={value.zip} onChange={set("zip")} /></div>
      <div className="col-span-2 space-y-1.5"><Label>Ort</Label><Input value={value.city} onChange={set("city")} /></div>
      <div className="space-y-1.5"><Label>Land</Label><Input maxLength={2} value={value.countryCode} onChange={set("countryCode")} /></div>
    </div>
  );
}

export function Suggestions({ data, onContact, onPlace }: {
  data?: SuggestOutput;
  onContact?: (c: { id: number; name: string }) => void;
  onPlace: (placeId: string) => void;
}) {
  if (!data) return null;
  return (
    <div className="space-y-2 text-sm">
      {onContact && data.sevdesk.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Schon in sevDesk</p>
          {data.sevdesk.map((c) => (
            <button key={c.id} type="button" className="block w-full rounded px-2 py-1 text-left hover:bg-accent" onClick={() => onContact(c)}>
              {c.name} <span className="text-muted-foreground">· verknüpfen</span>
            </button>
          ))}
        </div>
      )}
      {data.places.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground">Google Places</p>
          {data.places.map((p) => (
            <button key={p.placeId} type="button" className="block w-full rounded px-2 py-1 text-left hover:bg-accent" onClick={() => onPlace(p.placeId)}>
              {p.primary} <span className="text-muted-foreground">{p.secondary}</span>
            </button>
          ))}
        </div>
      )}
      {data.warnings.map((w) => <p key={w} className="text-xs text-muted-foreground">{w}</p>)}
    </div>
  );
}
